import { Grid, Stack } from "@mantine/core"
import { LabelWithTooltip, NumberInput } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import type { FormFieldConfig } from "~/pages/Admin/components/form.types"
import type { errorCodes } from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"

import { useCoordinatesField } from "./useCoordinatesField"

type CoordinatesInputFieldInnerProps = {
  readonly fields: FormFieldConfig
  readonly form: Parameters<typeof useCoordinatesField>[0]["form"]
  readonly translations: errorCodes | undefined
}

function CoordinatesInputFieldInner({
  fields,
  form,
  translations,
}: CoordinatesInputFieldInnerProps) {
  const { t } = useTranslation("common")
  const {
    latitude,
    longitude,
    onLatitudeChange,
    onLongitudeChange,
    onBlur,
    latitudeError,
    longitudeError,
    disabled,
  } = useCoordinatesField({
    form,
    fieldName: fields.name,
    translations,
    disabled: fields.disabled,
  })

  return (
    <Stack gap="xs">
      <Grid gutter="xs">
        <Grid.Col span={6}>
          <Stack gap="var(--mantine-spacing-xxs)">
            <LabelWithTooltip
              label={t("coordinates.latitude")}
              hoverHelperText={t("coordinates.latitudeTooltip")}
              justify="space-between"
              fw={600}
            />
            <NumberInput
              label={undefined}
              placeholder={t("coordinates.latitudePlaceholder")}
              value={
                latitude !== null && latitude !== undefined ? latitude : ""
              }
              onChange={onLatitudeChange}
              onBlur={onBlur}
              size="xs"
              step={0.0001}
              decimalScale={6}
              allowNegative
              hideControls
              error={latitudeError}
              disabled={disabled}
              labelProps={{
                fz: "xs",
                fw: 600,
              }}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={6}>
          <Stack gap="var(--mantine-spacing-xxs)">
            <LabelWithTooltip
              label={t("coordinates.longitude")}
              hoverHelperText={t("coordinates.longitudeTooltip")}
              justify="space-between"
              fw={600}
            />
            <NumberInput
              label={undefined}
              placeholder={t("coordinates.longitudePlaceholder")}
              value={
                longitude !== null && longitude !== undefined ? longitude : ""
              }
              onChange={onLongitudeChange}
              onBlur={onBlur}
              size="xs"
              step={0.0001}
              decimalScale={6}
              allowNegative
              hideControls
              error={longitudeError}
              disabled={disabled}
              labelProps={{
                fz: "xs",
                fw: 600,
              }}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

export function CoordinatesInputField(
  fields: FormFieldConfig,
  form: Parameters<typeof useCoordinatesField>[0]["form"],
  _error: keyof errorCodes | undefined,
  translations: errorCodes | undefined
): JSX.Element {
  return (
    <CoordinatesInputFieldInner
      fields={fields}
      form={form}
      translations={translations}
    />
  )
}
